<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import type { EChartsOption } from 'echarts'
import { pageById } from '@/data/registry'
import { usePrototypeStore } from '@/stores/prototype'
import ProTablePage from '@/components/pro/ProTablePage.vue'
import EChart from '@/components/ui/EChart.vue'
import AppIcon from '@/components/AppIcon.vue'
import { formatDateTime, formatNumber } from '@/utils/format'

const definition = pageById('medication-plans')
if (!definition) throw new Error('用药计划页面定义缺失')

const store = usePrototypeStore()
const router = useRouter()
const db = store.db

const plans = computed(() => db.medication_plans)
const records = computed(() => db.medication_records)

const weekDays = computed(() => Array.from({ length: 7 }, (_, index) => {
  const date = new Date('2026-10-17T12:00:00+08:00')
  date.setDate(date.getDate() - (6 - index))
  return {
    key: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`,
    label: `${date.getMonth() + 1}/${date.getDate()}`,
  }
}))

const trend = computed(() => weekDays.value.map(day => {
  const rows = records.value.filter(record => String(record.scheduled_at ?? '').slice(0, 10) === day.key)
  const taken = rows.filter(record => record.status === 'taken').length
  const overdue = rows.filter(record => record.status === 'overdue').length
  return { ...day, taken, overdue, total: rows.length, rate: rows.length ? Math.round((taken / rows.length) * 100) : 0 }
}))

const rateOption = computed<EChartsOption>(() => ({
  animationDuration: 480,
  grid: { left: 34, right: 10, top: 16, bottom: 24 },
  tooltip: { trigger: 'axis', backgroundColor: '#fff', borderColor: '#e8eef4', textStyle: { color: '#1f2d45', fontSize: 11 } },
  legend: { show: false },
  xAxis: { type: 'category', data: trend.value.map(item => item.label), axisTick: { show: false }, axisLine: { lineStyle: { color: '#e8eef4' } }, axisLabel: { color: '#9aa8bd', fontSize: 9 } },
  yAxis: { type: 'value', max: 100, splitNumber: 4, axisLabel: { color: '#9aa8bd', fontSize: 9, formatter: '{value}%' }, splitLine: { lineStyle: { color: '#f0f4f8', type: 'dashed' } } },
  series: [{
    name: '完成率', type: 'line', smooth: 0.35, data: trend.value.map(item => item.rate),
    symbol: 'circle', symbolSize: 5, lineStyle: { color: '#08b578', width: 2.2 }, itemStyle: { color: '#08b578', borderColor: '#fff', borderWidth: 2 },
    areaStyle: { color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: 'rgba(8,181,120,.2)' }, { offset: 1, color: 'rgba(8,181,120,.01)' }] } },
  }],
}))

const todayPlans = computed(() => [...plans.value]
  .filter(plan => ['active', 'overdue', 'paused'].includes(String(plan.status)))
  .sort((left, right) => String(left.next_remind_at ?? '').localeCompare(String(right.next_remind_at ?? '')))
  .slice(0, 5))

const overdueRecords = computed(() => records.value.filter(record => record.status === 'overdue' || record.status === 'pending').slice(0, 5))
const takenRate = computed(() => {
  const rows = records.value
  return rows.length ? Math.round((rows.filter(record => record.status === 'taken').length / rows.length) * 100) : 0
})

const batchTasks = computed(() => [
  ...overdueRecords.value.slice(0, 3).map(record => ({
    id: `record-${record.id}`,
    tone: record.status === 'overdue' ? 'danger' : 'warning',
    title: `${record.user_name} · ${record.drug_name}`,
    meta: record.status === 'overdue' ? '已超过提醒时间未确认' : '等待用户确认',
    time: formatDateTime(record.scheduled_at),
    count: 1,
  })),
  ...plans.value.filter(plan => plan.status === 'paused').slice(0, 1).map(plan => ({
    id: `plan-${plan.id}`,
    tone: 'normal',
    title: `${plan.user_name} · ${plan.drug_name}`,
    meta: '计划暂停中，恢复后继续提醒',
    time: '暂停中',
    count: 1,
  })),
])

function go(path: string): void {
  void router.push(path)
}
</script>

<template>
  <ProTablePage :definition="definition">
    <template #after-table>
      <section class="med-panels" aria-label="用药执行与批量提醒">
        <article class="card med-panel med-panel--trend">
          <header class="card__head">
            <div>
              <h2 class="card__title">用药完成趋势</h2>
              <p class="card__sub">按计划时间统计最近7天执行情况</p>
            </div>
            <div class="med-kpis">
              <span><b>{{ takenRate }}%</b><small>总体完成率</small></span>
              <span><b>{{ records.filter(record => record.status === 'taken').length }}</b><small>已确认</small></span>
              <span class="is-danger"><b>{{ records.filter(record => record.status === 'overdue').length }}</b><small>已逾期</small></span>
            </div>
          </header>
          <EChart :option="rateOption" height="176px" aria-label="近7日用药计划完成率折线图" />
          <div class="trend-legend"><span><i class="dot dot--green" />完成率</span><span class="muted">共 {{ formatNumber(plans.length) }} 个用药计划</span></div>
        </article>

        <article class="card med-panel">
          <header class="card__head">
            <div>
              <h2 class="card__title">今日提醒时间线</h2>
              <p class="card__sub">即将到达与逾期的提醒</p>
            </div>
            <button type="button" class="card__link" @click="go('/medication/records')">查看全部 <AppIcon name="chevron-right" :size="12" /></button>
          </header>
          <div class="timeline">
            <button v-for="plan in todayPlans" :key="String(plan.id)" type="button" class="timeline__row" @click="go('/medication/plans')">
              <span class="timeline__time">{{ formatDateTime(plan.next_remind_at) }}</span>
              <span class="timeline__dot" :class="plan.status === 'overdue' ? 'is-danger' : plan.status === 'paused' ? 'is-muted' : 'is-success'" />
              <span class="timeline__main">
                <b>{{ plan.user_name }} · {{ plan.drug_name }}</b>
                <small>{{ plan.dosage }} · {{ Array.isArray(plan.timing) ? plan.timing.join('、') : plan.timing }}</small>
              </span>
              <span class="tag" :class="plan.status === 'overdue' ? 'tag--danger' : plan.status === 'paused' ? 'tag--neutral' : plan.status === 'completed' ? 'tag--info' : 'tag--success'">
                {{ plan.status === 'overdue' ? '已逾期' : plan.status === 'paused' ? '已暂停' : plan.status === 'completed' ? '已完成' : '待提醒' }}
              </span>
            </button>
            <div v-if="!todayPlans.length" class="empty-state" style="min-height: 150px"><h3 class="empty-state__title">今天没有待执行计划</h3><p class="empty-state__text">新建计划后提醒会出现在这里。</p></div>
          </div>
        </article>

        <article class="card med-panel">
          <header class="card__head">
            <div>
              <h2 class="card__title">批量提醒任务 <span class="tag tag--danger">{{ batchTasks.length }}</span></h2>
              <p class="card__sub">需要运营介入的提醒</p>
            </div>
            <button type="button" class="card__link" @click="go('/medication/records')">查看全部 <AppIcon name="chevron-right" :size="12" /></button>
          </header>
          <div class="batch-list">
            <button v-for="task in batchTasks" :key="task.id" type="button" class="batch-row" @click="go('/medication/plans')">
              <span class="tag" :class="task.tone === 'danger' ? 'tag--danger' : task.tone === 'warning' ? 'tag--warning' : 'tag--neutral'">{{ task.tone === 'danger' ? '高风险' : task.tone === 'warning' ? '中优先' : '普通' }}</span>
              <span class="batch-row__main"><b>{{ task.title }}</b><small>{{ task.meta }}</small></span>
              <span class="batch-row__time">{{ task.time }}</span>
            </button>
          </div>
          <div class="med-actions">
            <button type="button" class="med-action" @click="go('/medication/plans')"><span class="med-action__icon med-action__icon--green"><AppIcon name="plus" :size="16" /></span><span><b>新建用药计划</b><small>为家庭成员创建用药方案</small></span></button>
            <button type="button" class="med-action" @click="go('/medication/records')"><span class="med-action__icon med-action__icon--blue"><AppIcon name="records" :size="16" /></span><span><b>查看用药记录</b><small>核对确认与逾期记录</small></span></button>
          </div>
        </article>
      </section>
    </template>
  </ProTablePage>
</template>

<style scoped>
.med-panels { display: grid; grid-template-columns: minmax(0, 1.12fr) minmax(0, 1fr) minmax(0, 1.08fr); gap: 14px; margin-top: 14px; }
.med-panel { min-width: 0; overflow: hidden; padding-bottom: 10px; }
.med-kpis { margin-left: auto; display: flex; gap: 10px; }
.med-kpis span { display: flex; flex-direction: column; align-items: flex-end; }
.med-kpis b { font-family: var(--font-num); font-size: 15px; color: var(--c-brand-strong); line-height: 1.2; }
.med-kpis small { font-size: 8.5px; color: var(--c-faint); }
.med-kpis .is-danger b { color: var(--c-danger); }
.med-panel :deep(.chart-box) { padding: 0 8px; }
.trend-legend { display: flex; align-items: center; gap: 12px; padding: 2px 16px 4px; font-size: 9px; color: var(--c-muted); }
.timeline { padding: 0 16px 6px; }
.timeline__row { width: 100%; display: flex; align-items: center; gap: 8px; padding: 7px 0; border: 0; border-top: 1px solid var(--c-divider); background: transparent; text-align: left; }
.timeline__row:first-child { border-top: 0; }
.timeline__time { width: 92px; flex: 0 0 auto; font-family: var(--font-num); font-size: 10px; color: var(--c-muted); }
.timeline__dot { width: 7px; height: 7px; flex: 0 0 auto; border-radius: 50%; background: var(--c-brand); box-shadow: 0 0 0 3px var(--c-brand-soft); }
.timeline__dot.is-danger { background: var(--c-danger); box-shadow: 0 0 0 3px var(--c-danger-soft); }
.timeline__dot.is-muted { background: var(--c-faint); box-shadow: 0 0 0 3px #f2f5f8; }
.timeline__main { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.timeline__main b { font-size: 10px; color: var(--c-text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.timeline__main small { margin-top: 2px; font-size: 8.5px; color: var(--c-faint); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.timeline .tag { flex: 0 0 auto; font-size: 8px; height: 18px; }
.batch-list { padding: 0 16px 4px; }
.batch-row { width: 100%; display: flex; align-items: center; gap: 7px; padding: 7px 0; border: 0; border-top: 1px solid var(--c-divider); background: transparent; text-align: left; }
.batch-row:first-child { border-top: 0; }
.batch-row__main { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.batch-row__main b { font-size: 10px; color: var(--c-text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.batch-row__main small { margin-top: 2px; font-size: 8.5px; color: var(--c-faint); }
.batch-row__time { font-size: 8.5px; color: var(--c-faint); white-space: nowrap; }
.batch-row .tag { font-size: 8px; height: 18px; padding: 0 5px; }
.med-actions { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; padding: 6px 16px 2px; border-top: 1px solid var(--c-divider); margin-top: 4px; }
.med-action { display: flex; align-items: center; gap: 6px; padding: 7px; border: 1px solid var(--c-border); border-radius: 8px; background: #fff; text-align: left; }
.med-action:hover { border-color: var(--c-success-border); background: var(--c-brand-mist); }
.med-action__icon { width: 26px; height: 26px; display: grid; place-items: center; border-radius: 7px; flex: 0 0 auto; }
.med-action__icon--green { background: var(--c-brand-soft); color: var(--c-brand-strong); }
.med-action__icon--blue { background: var(--c-info-soft); color: var(--c-info); }
.med-action b { display: block; font-size: 9.5px; color: var(--c-text); }
.med-action small { display: block; margin-top: 1px; font-size: 8px; color: var(--c-faint); }
@media (max-width: 1399px) { .med-panels { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 1023px) { .med-panels { grid-template-columns: minmax(0, 1fr); } }
</style>
